import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { coursesApi } from '@/lib/api';
import { formatPrice } from '@/lib/utils';

function Ic({ type, size = 20, color = 'var(--soleil-gold)' }: { type: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const icons: Record<string, React.ReactNode> = {
    book: <svg {...p}><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15z" /></svg>,
    clock: <svg {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    arrow: <svg {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>,
    star: <svg {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill={color} stroke="none" /></svg>,
  };
  return <>{icons[type] || null}</>;
}

function IcBox({ type, color, bg, size = 44 }: { type: string; color: string; bg: string; size?: number }) {
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.26, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Ic type={type} size={size * 0.48} color={color} />
    </div>
  );
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function SoleilCoursesPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const { data: courses, isLoading } = useQuery({
    queryKey: ['courses'],
    queryFn: () => coursesApi.list(),
  });

  if (isLoading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--soleil-cream)' }}>
        <div style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-mut)' }}>Loading...</div>
      </div>
    );
  }

  return (
    <section style={{ minHeight: '80vh', background: 'var(--soleil-cream)', padding: '56px 24px 72px' }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: courses && courses.length > 0 ? 48 : 0 }}>
          {courses && courses.length > 0 && (
            <>
              <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-sienna)', letterSpacing: '0.2em', marginBottom: 14 }}>
                {isRTL ? 'برامجنا' : 'OUR PROGRAMS'}
              </div>
              <h1 style={{ fontFamily: S.heading, fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 600, color: 'var(--soleil-ink)' }}>
                {isRTL ? 'دورات ' : 'Professional '}
                <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
                  {isRTL ? 'الشهادات المهنية' : 'certification'}
                </span>
                {isRTL ? '' : ' courses'}
              </h1>
            </>
          )}
        </div>

        {/* Course Grid */}
        {courses && courses.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: courses.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 20 }}>
            {courses.map((course: any, i: number) => {
              const isFeatured = course.isFeatured;
              return (
                <div key={course.id} style={{
                  background: '#fff',
                  border: `1px solid ${isFeatured ? 'rgba(184,148,58,0.26)' : 'var(--soleil-sand)'}`,
                  borderRadius: 20, overflow: 'hidden', position: 'relative',
                  boxShadow: isFeatured ? '0 8px 32px rgba(184,148,58,0.08)' : 'none',
                }}>
                  {isFeatured && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--soleil-gold), var(--soleil-sienna))' }} />}
                  {isFeatured && (
                    <div style={{ background: 'var(--soleil-gold-pale)', padding: 10, textAlign: 'center', borderBottom: '1px solid rgba(184,148,58,0.12)' }}>
                      <span style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-gold-dark)', letterSpacing: '0.1em' }}>
                        ★ {isRTL ? 'الأكثر شعبية' : 'MOST POPULAR'}
                      </span>
                    </div>
                  )}
                  <div style={{ padding: '32px 28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <IcBox
                        type="book"
                        color={isFeatured ? 'var(--soleil-gold)' : 'var(--soleil-mut)'}
                        bg={isFeatured ? 'var(--soleil-gold-pale)' : 'var(--soleil-cream)'}
                      />
                      <h3 style={{ fontFamily: S.heading, fontSize: 24, fontWeight: 600, color: 'var(--soleil-ink)' }}>
                        {isRTL ? course.titleAr : course.titleEn}
                      </h3>
                    </div>
                    <p style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-sub)', lineHeight: 1.7, marginBottom: 24 }}>
                      {isRTL ? course.shortDescriptionAr : course.shortDescriptionEn}
                    </p>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid var(--soleil-sand)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Ic type="book" size={14} color="var(--soleil-mut)" />
                        <span style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)' }}>
                          {course.totalLessons || 0} {isRTL ? 'درس' : 'lessons'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Ic type="clock" size={14} color="var(--soleil-mut)" />
                        <span style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)' }}>
                          {course.durationHours || 0} {isRTL ? 'ساعة' : 'hours'}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontFamily: S.heading, fontSize: 22, fontWeight: 600, color: course.price > 0 ? 'var(--soleil-ink)' : 'var(--soleil-mut)' }}>
                        {course.price > 0 ? formatPrice(course.price, course.currency) : (isRTL ? 'قريباً' : 'Coming soon')}
                      </span>
                      <Link
                        to={`/courses/${course.slug}`}
                        style={{
                          fontFamily: S.font, fontSize: 13, fontWeight: 600, borderRadius: 10,
                          padding: '11px 26px', cursor: 'pointer', textDecoration: 'none',
                          ...(isFeatured
                            ? { background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))', color: 'var(--soleil-dark)', border: 'none', boxShadow: '0 2px 12px rgba(184,148,58,0.2)' }
                            : { background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))', color: 'var(--soleil-dark)', border: 'none', boxShadow: '0 2px 12px rgba(184,148,58,0.15)' }),
                        }}
                      >
                        {course.price > 0 ? (isRTL ? 'سجّل الآن' : 'Enroll now') : (isRTL ? 'أعلمني' : 'Notify me')}
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 24px 80px' }}>
            {/* Sun icon */}
            <div style={{ marginBottom: 24 }}>
              <svg width="80" height="80" viewBox="0 0 64 64" fill="none" style={{ opacity: 0.9 }}>
                <circle cx="32" cy="32" r="12" stroke="var(--soleil-gold)" strokeWidth="2.5" fill="none" />
                <g stroke="var(--soleil-gold)" strokeWidth="2" strokeLinecap="round">
                  <line x1="32" y1="10" x2="32" y2="16" /><line x1="32" y1="48" x2="32" y2="54" />
                  <line x1="10" y1="32" x2="16" y2="32" /><line x1="48" y1="32" x2="54" y2="32" />
                  <line x1="16.4" y1="16.4" x2="20.6" y2="20.6" /><line x1="43.4" y1="43.4" x2="47.6" y2="47.6" />
                  <line x1="16.4" y1="47.6" x2="20.6" y2="43.4" /><line x1="43.4" y1="20.6" x2="47.6" y2="16.4" />
                </g>
              </svg>
            </div>

            {/* Main title — big and bold */}
            <h1 style={{
              fontFamily: S.heading, fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 700,
              color: 'var(--soleil-ink)', marginBottom: 16, lineHeight: 1.2,
            }}>
              ITIL <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>5</span> Foundation
            </h1>

            {/* Coming Soon badge — prominent */}
            <div style={{
              display: 'inline-block', padding: '8px 24px', borderRadius: 20,
              background: 'transparent', border: '2px solid var(--soleil-gold)',
              fontFamily: S.font, fontSize: 13, fontWeight: 700, color: 'var(--soleil-gold)',
              letterSpacing: '0.15em', marginBottom: 24,
            }}>
              {isRTL ? 'قريباً' : 'COMING SOON'}
            </div>

            {/* Description */}
            <p style={{
              fontFamily: S.font, fontSize: 16, color: 'var(--soleil-sub)', lineHeight: 1.7,
              maxWidth: 460, margin: '0 auto 36px',
            }}>
              {isRTL
                ? 'نحن نعمل على إعداد دورة ITIL 5 Foundation الشاملة. كن أول من يعلم عند الإطلاق!'
                : 'We\'re preparing a comprehensive ITIL 5 Foundation course. Be the first to know when we launch!'}
            </p>

            {/* Contact CTA */}
            <Link to="/contact" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 32px',
              background: 'var(--soleil-gold)',
              color: '#fff', borderRadius: 11, fontFamily: S.font, fontSize: 14, fontWeight: 600,
              textDecoration: 'none', boxShadow: '0 4px 16px rgba(46,42,38,0.15)',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(46,42,38,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(46,42,38,0.15)'; }}
            >
              {isRTL ? 'تواصل معنا للمزيد' : 'Contact Us to Learn More'}
              <Ic type="arrow" size={16} color="#fff" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
